import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

const LINES = ["Household, wardrobe,", "and gadget finds", "worth a second look."];

const lineReveal = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.15 + i * 0.12 },
  }),
};

export const Hero = ({ count }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yMark = useTransform(scrollYProgress, [0, 1], [0, 140]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden border-b hairline"
    >
      <motion.span
        aria-hidden="true"
        style={{ y: yMark }}
        className="pointer-events-none select-none absolute -right-8 -top-16 font-serif italic text-[22rem] leading-none text-charcoal/[0.05]"
      >
        F.
      </motion.span>

      <div className="max-w-6xl mx-auto px-6 pt-20 pb-16 sm:pt-28 sm:pb-24">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="font-mono text-[11px] uppercase tracking-[0.3em] text-brass mb-8"
          data-testid="hero-eyebrow"
        >
          Curated picks, not our own inventory
        </motion.p>

        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08] text-charcoal max-w-3xl">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                custom={i}
                variants={lineReveal}
                initial="hidden"
                animate="show"
              >
                {i === 2 ? <em className="text-brass not-italic font-serif italic">{line}</em> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 max-w-xl text-sm sm:text-base leading-relaxed text-charcoal/70"
          data-testid="hero-subtext"
        >
          Findora is a curated collection of interesting products we think are
          worth your attention. We keep the list small and focus on finds
          across home, style, gadgets and everyday essentials. When something
          catches your eye, follow the link to the marketplace where it's sold
          and buy it directly there at the regular listed price.
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 font-mono text-[11px] uppercase tracking-[0.25em] text-charcoal/50"
        >
          <span data-testid="hero-marketplace-badge" className="flex items-center gap-2">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-brass" />
            Marketplace · Amazon
          </span>
          <span data-testid="hero-item-count">{count} items shortlisted</span>
        </motion.div>
      </div>
    </section>
  );
};
